import { inject, Injectable, NgZone } from '@angular/core';
import {
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import Tracker, { type Options as TrackerOptions } from '@openreplay/tracker';
import trackerAssist from '@openreplay/tracker-assist';
import { environment } from '@env/environment';
import { UserAuthorizationService } from '../user-authorization.service';
import {
  ENVIRONMENT_NAMES,
  LOCAL_STORAGE_KEYS,
} from '@app/shared/constants/constants';

const OPEN_REPLAY_REDACTED_VALUE = '[REDACTED]';

const OPEN_REPLAY_SENSITIVE_HEADERS = [
  'authorization',
  'cookie',
  'set-cookie',
] as const;

const OPEN_REPLAY_SENSITIVE_BODY_KEYS = [
  'password',
  'token',
  'access_token',
  'refresh_token',
  'accesstoken',
  'refreshtoken',
  'secret',
  'apikey',
  'api_key',
] as const;

interface OpenReplayNetworkPayload {
  readonly status: number;
  readonly method: string;
  url: string;
  request: {
    body: string | null;
    headers: Record<string, string>;
  };
  response: {
    body: unknown;
    headers: Record<string, string>;
  };
}

@Injectable({
  providedIn: 'root',
})
// This service was created using the OpenReplay Angular SDK https://docs.openreplay.com/en/sdk/using-or/angular/
export class OpenReplayService {
  public tracker?: Tracker | null;
  private readonly zone = inject(NgZone);
  private readonly userAuthorizationService = inject(UserAuthorizationService);
  constructor() {
    this.initTracker();
  }

  private initTracker(): void {
    if (
      environment.environmentName === ENVIRONMENT_NAMES.LOCAL ||
      environment.environmentName === ENVIRONMENT_NAMES.DEVELOPMENT
    )
      return;
    this.zone.runOutsideAngular(() => {
      const trackerOptions: Partial<TrackerOptions> = {
        projectKey: environment.openReplay.projectKey,
        network: {
          capturePayload: true,
          sanitizer: data =>
            this.sanitizeNetworkPayload(
              data as OpenReplayNetworkPayload,
            ) as typeof data,
        } as NonNullable<TrackerOptions['network']>,
        // Disable secure mode to allow local testing of the tracker
        // __DISABLE_SECURE_MODE: true,
      };
      this.tracker = new Tracker(trackerOptions);

      // Optional plugins
      this.tracker.use(
        // With this plugin can be called the user
        trackerAssist({
          confirmText: `You have an incoming call from Stirista Support. Do you want to answer?`,
        }),
      );
    });
  }

  checkTrackerIsActive() {
    return this.tracker?.isActive();
  }

  public async start() {
    if (
      environment.environmentName === ENVIRONMENT_NAMES.LOCAL ||
      environment.environmentName === ENVIRONMENT_NAMES.DEVELOPMENT
    )
      return;
    this.zone.runOutsideAngular(() => {
      if (this.tracker) {
        const userData =
          this.userAuthorizationService.userData() ||
          JSON.parse(
            localStorage.getItem(LOCAL_STORAGE_KEYS.USER_DATA) || '{}',
          );

        const tenantId =
          this.userAuthorizationService.getCurrentTenant || userData?.tenantId;

        const orgId =
          this.userAuthorizationService.currentOrg() || userData?.orgId;

        return this.tracker.start().then(() => {
          // Set labels, before adding in code please make sure they are configured in preferences/projects/metadata
          this.tracker?.setMetadata('environment', environment.environmentName);
          if (tenantId) {
            this.tracker?.setMetadata('tenantId', tenantId);
          }
          if (orgId) {
            this.tracker?.setMetadata('orgId', orgId);
          }
        });
      } else {
        return {
          sessionID: null,
          sessionToken: null,
          userUUID: null,
        };
      }
    });
  }

  restartTracking(userId?: string, callback?: () => void): void {
    if (
      environment.environmentName === ENVIRONMENT_NAMES.LOCAL ||
      environment.environmentName === ENVIRONMENT_NAMES.DEVELOPMENT
    )
      return;
    if (this.tracker) {
      this.tracker.stop(); // Stop the current tracking
    }

    if (!userId) return;

    // Reinitialize tracking
    this.start().then(() => {
      this.setUserData({ id: userId });
      callback && callback();
    });
  }

  public setUserData(user: { id: string }): void {
    this.zone.runOutsideAngular(() => {
      if (this.tracker && user.id) {
        this.tracker.setUserID(String(user.id));
      }
    });
  }

  /**
   * Sends HTTP request/response details to the active OpenReplay session as custom events.
   *
   * @param event - Base event name, typically the request URL.
   * @param params - Angular HTTP request and response pair to serialize for telemetry.
   */
  public sendEventToReplaySession(
    event: string,
    params: { request: HttpRequest<unknown>; response: HttpResponse<unknown> },
  ): void {
    const { request, response } = params;

    this.tracker?.event(event + '[request]', {
      method: request.method,
      url: request.url,
      params: this.serializeQueryParams(request.params),
      body: this.redactSensitiveFieldsInPayload(request.body),
    });
    this.tracker?.event(event + '[response]', {
      status: response.status,
      headers: this.serializeHttpHeaders(response.headers),
      body: this.redactSensitiveFieldsInPayload(response.body),
    });
  }

  /**
   * Redacts sensitive headers and body fields from OpenReplay network payloads.
   *
   * @param data - Raw network request/response data captured by the tracker.
   * @returns Sanitized payload safe to store in OpenReplay.
   */
  private sanitizeNetworkPayload(
    data: OpenReplayNetworkPayload,
  ): OpenReplayNetworkPayload {
    return {
      ...data,
      request: {
        body: this.redactSensitiveFieldsInPayload(data.request.body) as
          | string
          | null,
        headers: this.redactSensitiveHeaders(data.request.headers),
      },
      response: {
        body: this.redactSensitiveFieldsInPayload(data.response.body),
        headers: this.redactSensitiveHeaders(data.response.headers),
      },
    };
  }

  /**
   * Converts Angular query params into a plain query string for telemetry events.
   *
   * @param params - Angular HttpParams from the outgoing request.
   * @returns Serialized query string without a leading question mark.
   */
  private serializeQueryParams(params: HttpParams): string {
    return params.toString();
  }

  /**
   * Converts Angular response headers into a plain object and redacts sensitive values.
   *
   * @param headers - Angular HttpHeaders from the HTTP response.
   * @returns Serializable header map with sensitive values redacted.
   */
  private serializeHttpHeaders(headers: HttpHeaders): Record<string, string> {
    const serialized: Record<string, string> = {};
    headers.keys().forEach(key => {
      const value = headers.get(key);
      if (value !== null) {
        serialized[key] = value;
      }
    });
    return this.redactSensitiveHeaders(serialized);
  }

  /**
   * Replaces sensitive header values with a redacted placeholder.
   *
   * @param headers - Header map captured from an HTTP request or response.
   * @returns Header map with sensitive values redacted.
   */
  private redactSensitiveHeaders(
    headers: Record<string, string>,
  ): Record<string, string> {
    const redacted: Record<string, string> = { ...headers };
    for (const key of Object.keys(redacted)) {
      if (this.isSensitiveHeader(key)) {
        redacted[key] = OPEN_REPLAY_REDACTED_VALUE;
      }
    }
    return redacted;
  }

  /**
   * Recursively redacts known sensitive fields from request/response bodies.
   *
   * @param body - Raw request or response body value.
   * @returns Body value with sensitive fields replaced by a redacted placeholder.
   */
  private redactSensitiveFieldsInPayload(body: unknown): unknown {
    if (body === null || body === undefined) {
      return body;
    }

    if (typeof body === 'string') {
      try {
        const parsed: unknown = JSON.parse(body);
        return JSON.stringify(this.redactSensitiveFieldsInPayload(parsed));
      } catch {
        return body;
      }
    }

    if (Array.isArray(body)) {
      return body.map(item => this.redactSensitiveFieldsInPayload(item));
    }

    if (typeof body === 'object') {
      const record = body as Record<string, unknown>;
      const redacted: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(record)) {
        redacted[key] = this.isSensitiveBodyKey(key)
          ? OPEN_REPLAY_REDACTED_VALUE
          : this.redactSensitiveFieldsInPayload(value);
      }
      return redacted;
    }

    return body;
  }

  /**
   * Checks whether a header name should be redacted before telemetry storage.
   *
   * @param headerName - HTTP header name.
   * @returns True when the header contains sensitive auth/session data.
   */
  private isSensitiveHeader(headerName: string): boolean {
    const normalizedHeaderName = headerName.toLowerCase();
    return OPEN_REPLAY_SENSITIVE_HEADERS.some(
      sensitiveHeader => sensitiveHeader === normalizedHeaderName,
    );
  }

  /**
   * Checks whether a body field name should be redacted before telemetry storage.
   *
   * @param fieldName - JSON object property name.
   * @returns True when the field is known to contain sensitive credentials or tokens.
   */
  private isSensitiveBodyKey(fieldName: string): boolean {
    const normalizedFieldName = fieldName.toLowerCase().replace(/_/g, '');
    return OPEN_REPLAY_SENSITIVE_BODY_KEYS.some(sensitiveKey => {
      const normalizedSensitiveKey = sensitiveKey.replace(/_/g, '');
      return normalizedSensitiveKey === normalizedFieldName;
    });
  }
}
